import PouchDB from 'pouchdb-browser';
import { useSaleStore } from '@/store/sale.store';
import { useSyncStore, SyncResult } from '@/store/sync.store';
import { useConnectionStore } from '@/store/connection.store';
import { CAJA_CERRADA, tryHandleCajaCerradaFromHttp } from '@/utilities/caja-cerrada.utils';
import { syncUnsyncedStoreSales } from '@/utilities/sync-unsynced-store-sales';
import { isOfflineUuidSyncedInStore } from '@/utilities/sales-dedup.utils';
import {
  buildApiHeadersFromSession,
  canRunOfflineSync,
  waitForSyncPrerequisites,
} from '@/utilities/sync-session.utils';

// Tipos de estado de sincronización
export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'error' | 'error_fatal';

// Configuración de reintentos
import { MAX_OFFLINE_SYNC_ATTEMPTS } from "@/utilities/offline-sync-retry.constants";

const MAX_RETRY_ATTEMPTS = MAX_OFFLINE_SYNC_ATTEMPTS;
const FATAL_ERROR_CODES = [400, 422, 404]; // Errores que no deben reintentarse

interface OfflinePost {
    _id: string;
    _rev?: string; // PouchDB revision field
    url: string;
    headers: Record<string, string>;
    body: any;
    // Campos para control de sincronización
    sync_status: SyncStatus;
    sync_attempts: number;
    last_error_message: string | null;
    last_error_code: number | null;
    created_at: number;
    last_attempt_at: number | null;
    // Tipo de registro para el monitor
    record_type?: 'sale' | 'cancellation' | 'refund';
}

const db = new PouchDB<OfflinePost>('offline-posts');

/**
 * Determina el tipo de registro basándose en la URL
 */
function getRecordType(url: string): 'sale' | 'cancellation' | 'refund' {
  if (url.includes('cancel') || url.includes('annul')) return 'cancellation';
  if (url.includes('refund')) return 'refund';
  return 'sale';
}

async function findPendingSalePostByOfflineUuid(
  offlineUuid: string
): Promise<OfflinePost | undefined> {
  try {
    const allDocs = await db.allDocs({ include_docs: true });
    return allDocs.rows
      .map((row) => row.doc as OfflinePost | undefined)
      .find(
        (doc) =>
          doc !== undefined &&
          doc.url.includes("sales") &&
          doc.body?.offline_uuid === offlineUuid &&
          doc.sync_status !== "synced"
      );
  } catch {
    return undefined;
  }
}

/**
 * Elimina cola PouchDB de una venta ya sincronizada en el store (evita doble POST).
 */
export async function removePouchPostsForOfflineUuid(
  offlineUuid: string
): Promise<number> {
  let removed = 0;
  try {
    const allDocs = await db.allDocs({ include_docs: true });
    for (const row of allDocs.rows) {
      const doc = row.doc as OfflinePost | undefined;
      if (
        !doc ||
        !doc.url.includes("sales") ||
        doc.body?.offline_uuid !== offlineUuid
      ) {
        continue;
      }
      if (doc._rev) {
        await db.remove(doc._id, doc._rev);
        removed++;
      }
    }
  } catch (error) {
    console.error("[OfflineDB] Error limpiando cola por offline_uuid:", error);
  }
  return removed;
}

async function skipPouchSalePostIfAlreadySynced(
  post: OfflinePost
): Promise<boolean> {
  const offlineUuid = post.body?.offline_uuid;
  if (!offlineUuid || !post.url.includes("sales")) {
    return false;
  }
  if (!isOfflineUuidSyncedInStore(offlineUuid)) {
    return false;
  }
  await removePost(post);
  return true;
}

/**
 * Guardar una solicitud POST en IndexedDB con headers y body
 */
export async function savePostRequest(url: string, headers: Record<string, string>, body: any) {
    const offlineUuid = body?.offline_uuid as string | undefined;

    if (offlineUuid && url.includes("sales")) {
      const existing = await findPendingSalePostByOfflineUuid(offlineUuid);
      if (existing) {
        try {
          const updated: OfflinePost = {
            ...existing,
            url,
            headers,
            body,
            sync_status: "pending",
            last_error_message: null,
            last_error_code: null,
            last_attempt_at: null,
            record_type: getRecordType(url),
          };
          await db.put(updated);
          console.log(
            "[OfflineDB] POST actualizado (mismo offline_uuid):",
            offlineUuid
          );
          return;
        } catch (error) {
          console.error("[OfflineDB] Error actualizando POST existente:", error);
        }
      }
    }

    const doc: OfflinePost = {
      _id: new Date().toISOString(),
      url,
      headers,
      body,
      sync_status: 'pending',
      sync_attempts: 0,
      last_error_message: null,
      last_error_code: null,
      created_at: Date.now(),
      last_attempt_at: null,
      record_type: getRecordType(url),
    };
  
    try {
      await db.put(doc);
      console.log('[OfflineDB] POST guardado offline correctamente:', url);
    } catch (error) {
      console.error('[OfflineDB] Error al guardar en PouchDB:', error);
    }
}

/**
 * Obtiene todos los registros pendientes de sincronización
 */
export async function getPendingRequests(): Promise<OfflinePost[]> {
  try {
    const allDocs = await db.allDocs({ include_docs: true });
    return allDocs.rows
      .map(row => row.doc as OfflinePost | undefined)
      .filter((doc): doc is OfflinePost => 
        doc !== undefined && 
        doc.sync_status !== 'synced' && 
        doc.sync_status !== 'error_fatal' &&
        doc.sync_status !== 'syncing'
      );
  } catch (error) {
    console.error('[OfflineDB] Error obteniendo pendientes:', error);
    return [];
  }
}

/**
 * Obtiene registros con error para mostrar en UI
 */
export async function getFailedRequests(): Promise<OfflinePost[]> {
  try {
    const allDocs = await db.allDocs({ include_docs: true });
    return allDocs.rows
      .map(row => row.doc as OfflinePost | undefined)
      .filter((doc): doc is OfflinePost => 
        doc !== undefined && 
        (doc.sync_status === 'error_fatal' || doc.sync_status === 'error')
      );
  } catch (error) {
    console.error('[OfflineDB] Error obteniendo fallidos:', error);
    return [];
  }
}

/**
 * Obtiene el conteo total de registros pendientes
 */
export async function getPendingCount(): Promise<number> {
  try {
    const allDocs = await db.allDocs({ include_docs: true });
    return allDocs.rows.filter(row => {
      const doc = row.doc as OfflinePost | undefined;
      return doc && doc.sync_status !== 'synced';
    }).length;
  } catch (error) {
    return 0;
  }
}

/**
 * Actualiza el estado de un registro
 */
async function updatePostStatus(
  post: OfflinePost, 
  status: SyncStatus, 
  errorMessage?: string,
  errorCode?: number
): Promise<void> {
  try {
    const updatedPost: OfflinePost = {
      ...post,
      sync_status: status,
      sync_attempts: post.sync_attempts + 1,
      last_error_message: errorMessage ?? post.last_error_message,
      last_error_code: errorCode ?? post.last_error_code,
      last_attempt_at: Date.now(),
    };
    await db.put(updatedPost);
  } catch (error) {
    console.error('[OfflineDB] Error actualizando estado:', error);
  }
}

/**
 * Elimina un registro después de sincronización exitosa
 */
export async function removePost(post: OfflinePost): Promise<void> {
  try {
    if (post._rev) {
      await db.remove(post._id, post._rev);
    }
    console.log('[OfflineDB] Registro eliminado:', post._id);
  } catch (error) {
    console.error('[OfflineDB] Error eliminando registro:', error);
  }
}

/**
 * Resetea un registro con error para permitir reintento manual
 */
export async function resetFailedPost(postId: string): Promise<boolean> {
  try {
    const post = await db.get(postId);
    const updatedPost: OfflinePost = {
      ...post,
      sync_status: 'pending',
      sync_attempts: 0,
      last_error_message: null,
      last_error_code: null,
      last_attempt_at: null,
    };
    await db.put(updatedPost);
    console.log('[OfflineDB] Registro reseteado para reintento:', postId);
    return true;
  } catch (error) {
    console.error('[OfflineDB] Error reseteando registro:', error);
    return false;
  }
}

/**
 * Elimina un registro fallido permanentemente
 */
export async function discardFailedPost(postId: string): Promise<boolean> {
  try {
    const post = await db.get(postId);
    await db.remove(post);
    console.log('[OfflineDB] Registro descartado:', postId);
    return true;
  } catch (error) {
    console.error('[OfflineDB] Error descartando registro:', error);
    return false;
  }
}

// Flag para evitar múltiples ejecuciones simultáneas
let isRetrying = false;

/**
 * Función de reintento automático con UI
 * Se ejecuta periódicamente cuando hay conexión
 * Muestra el monitor de sincronización para que el usuario vea el progreso
 */
export const retryOfflineRequests = async () => {
  if (!navigator.onLine || useConnectionStore.getState().detectedOffline) {
    return;
  }
  if (isRetrying) return;

  await waitForSyncPrerequisites();
  if (!canRunOfflineSync()) {
    return;
  }

  isRetrying = true;

  const syncStore = useSyncStore.getState();
  const saleStore = useSaleStore.getState();
  const storePending = saleStore.getUnsyncedSales();
  const storeAutoSync = saleStore.getUnsyncedSalesForAutoSync();

  try {
    const pendingPosts = await getPendingRequests();
    const pouchAutoSync = pendingPosts.filter(
      (post) => post.sync_attempts < MAX_RETRY_ATTEMPTS
    );
    const autoSyncTotal = storeAutoSync.length + pouchAutoSync.length;

    if (storePending.length === 0 && pendingPosts.length === 0) {
      return;
    }

    if (autoSyncTotal === 0) {
      return;
    }

    syncStore.startSync(autoSyncTotal);
    syncStore.minimize();

    const storeResult = await syncUnsyncedStoreSales();
    if (storeResult.aborted) {
      return;
    }

    const postsToSync = await getPendingRequests();

    for (const post of postsToSync) {
      const recordId = post._id;
      const recordType = post.record_type || 'sale';

      // Preparar datos para mostrar en el monitor
      const displayData: SyncResult['displayData'] = {
        amount: post.body?.total_amount || post.body?.bills?.[0]?.total_amount,
        customerName: post.body?.customer?.name,
        documentType: post.body?.bill_type?.name || post.body?.bills?.[0]?.bill_type?.name,
      };

      // Verificar si ya excedió el máximo de intentos
      if (post.sync_attempts >= MAX_RETRY_ATTEMPTS) {
        await updatePostStatus(post, 'error_fatal', 
          `Máximo de reintentos alcanzado (${MAX_RETRY_ATTEMPTS})`,
          0
        );
        // Reportar error al monitor
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'error',
          errorMessage: `Máximo de reintentos alcanzado (${MAX_RETRY_ATTEMPTS})`,
          errorCode: 0,
          displayData,
        });
        continue;
      }

      // Marcar como "syncing" en el monitor
      syncStore.markSyncing(recordId);

      // Marcar como "syncing" en PouchDB
      await updatePostStatus(post, 'syncing');

      if (await skipPouchSalePostIfAlreadySynced(post)) {
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'success',
          displayData,
        });
        continue;
      }

      const headers = buildApiHeadersFromSession(post.headers);
      const offlineUuid = post.body?.offline_uuid;
      const isSaleRequest = post.url.includes('sales');

      try {
        const response = await fetch(post.url, {
          method: 'POST',
          headers,
          body: JSON.stringify(post.body)
        });

        if (response.ok) {
          await removePost(post);
          
          if (offlineUuid && isSaleRequest) {
            try {
              const responseData = await response.json();
              const serverId = responseData?.id;
              if (serverId) {
                const { markSaleAsSynced } = useSaleStore.getState();
                markSaleAsSynced(offlineUuid, serverId);
              }
            } catch (parseError) {
              console.warn('[OfflineDB] No se pudo parsear respuesta:', parseError);
            }
          }

          // Reportar éxito al monitor
          syncStore.addResult({
            id: recordId,
            type: recordType,
            status: 'success',
            displayData,
          });
        } else {
          const errorBody = await response.text();

          if (
            tryHandleCajaCerradaFromHttp(response.status, errorBody, {
              requestBody: post.body,
            })
          ) {
            return;
          }

          let errorMessage = `HTTP ${response.status}`;
          
          try {
            const errorJson = JSON.parse(errorBody);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            errorMessage = errorBody.substring(0, 200) || errorMessage;
          }

          if (FATAL_ERROR_CODES.includes(response.status)) {
            await updatePostStatus(post, 'error_fatal', errorMessage, response.status);
          } else {
            await updatePostStatus(post, 'error', errorMessage, response.status);
          }

          // Reportar error al monitor
          syncStore.addResult({
            id: recordId,
            type: recordType,
            status: 'error',
            errorMessage,
            errorCode: response.status,
            displayData,
          });
        }
      } catch (networkError) {
        const errorMessage = networkError instanceof Error 
          ? networkError.message 
          : 'Error de red desconocido';
        await updatePostStatus(post, 'error', errorMessage, 0);

        // Reportar error al monitor
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'error',
          errorMessage,
          errorCode: 0,
          displayData,
        });
      }

      // Pequeña pausa para no saturar el servidor
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  } catch (err) {
    console.error('[OfflineDB] Error en retry:', err);
  } finally {
    isRetrying = false;
  }
};

/**
 * Sincronización masiva con monitor de progreso (UI)
 * Se usa cuando el usuario inicia sincronización manual
 */
export const syncAllWithProgress = async (options?: {
  skipStart?: boolean;
}): Promise<{
  success: number;
  errors: number;
}> => {
  await waitForSyncPrerequisites();
  if (!canRunOfflineSync()) {
    return { success: 0, errors: 0 };
  }

  const syncStore = useSyncStore.getState();
  const pendingPosts = await getPendingRequests();

  if (pendingPosts.length === 0) {
    if (!options?.skipStart) {
      syncStore.startSync(0);
    }
    return { success: 0, errors: 0 };
  }

  if (!options?.skipStart) {
    syncStore.startSync(pendingPosts.length);
  }

  let successCount = 0;
  let errorCount = 0;

  for (const post of pendingPosts) {
    const recordId = post._id;
    const recordType = post.record_type || 'sale';

    // Marcar como en proceso en el monitor
    syncStore.markSyncing(recordId);

    // Preparar datos para mostrar en el monitor
    const displayData: SyncResult['displayData'] = {
      amount: post.body?.total_amount || post.body?.bills?.[0]?.total_amount,
      customerName: post.body?.customer?.name,
      documentType: post.body?.bill_type?.name || post.body?.bills?.[0]?.bill_type?.name,
    };

    try {
      if (await skipPouchSalePostIfAlreadySynced(post)) {
        successCount++;
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'success',
          displayData,
        });
        continue;
      }

      const headers = buildApiHeadersFromSession(post.headers);
      const offlineUuid = post.body?.offline_uuid;
      const isSaleRequest = post.url.includes('sales');

      const response = await fetch(post.url, {
        method: 'POST',
        headers,
        body: JSON.stringify(post.body)
      });

      if (response.ok) {
        await removePost(post);
        successCount++;

        // Actualizar store de ventas si aplica
        if (offlineUuid && isSaleRequest) {
          try {
            const responseData = await response.json();
            const serverId = responseData?.id;
            if (serverId) {
              const { markSaleAsSynced } = useSaleStore.getState();
              markSaleAsSynced(offlineUuid, serverId);
            }
          } catch {
            // Ignorar error de parseo
          }
        }

        // Reportar éxito al monitor
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'success',
          displayData,
        });
      } else {
        const errorBody = await response.text();

        if (
          tryHandleCajaCerradaFromHttp(response.status, errorBody, {
            requestBody: post.body,
          })
        ) {
          return { success: successCount, errors: errorCount };
        }

        let errorMessage = `HTTP ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorBody);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          errorMessage = errorBody.substring(0, 200) || errorMessage;
        }

        // Determinar si es error fatal
        const newStatus: SyncStatus = FATAL_ERROR_CODES.includes(response.status)
          ? 'error_fatal'
          : 'error';

        await updatePostStatus(post, newStatus, errorMessage, response.status);
        errorCount++;

        // Reportar error al monitor
        syncStore.addResult({
          id: recordId,
          type: recordType,
          status: 'error',
          errorMessage,
          errorCode: response.status,
          displayData,
        });
      }
    } catch (networkError) {
      const errorMessage = networkError instanceof Error 
        ? networkError.message 
        : 'Error de red desconocido';
      
      await updatePostStatus(post, 'error', errorMessage, 0);
      errorCount++;

      // Reportar error al monitor
      syncStore.addResult({
        id: recordId,
        type: recordType,
        status: 'error',
        errorMessage,
        errorCode: 0,
        displayData,
      });
    }

    // Pequeña pausa para no saturar el servidor
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return { success: successCount, errors: errorCount };
};

/**
 * Reintenta un registro específico con reporte al monitor
 */
export const retrySpecificPost = async (postId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const post = await db.get(postId);
    
    // Actualizar estado a syncing
    const resetPost: OfflinePost = {
      ...post,
      sync_status: 'syncing',
      last_attempt_at: Date.now(),
    };
    await db.put(resetPost);

    if (await skipPouchSalePostIfAlreadySynced(post)) {
      return { success: true };
    }

    if (!canRunOfflineSync()) {
      return {
        success: false,
        error: "Abre la caja en la tienda antes de sincronizar.",
      };
    }

    const headers = buildApiHeadersFromSession(post.headers);
    const offlineUuid = post.body?.offline_uuid;
    const isSaleRequest = post.url.includes('sales');

    const response = await fetch(post.url, {
      method: 'POST',
      headers,
      body: JSON.stringify(post.body)
    });

    if (response.ok) {
      if (resetPost._rev) {
        await db.remove(resetPost._id, resetPost._rev);
      }
      
      if (offlineUuid && isSaleRequest) {
        try {
          const responseData = await response.json();
          const serverId = responseData?.id;
          if (serverId) {
            const { markSaleAsSynced } = useSaleStore.getState();
            markSaleAsSynced(offlineUuid, serverId);
          }
        } catch {
          // Ignorar error de parseo
        }
      }
      
      return { success: true };
    } else {
      const errorBody = await response.text();

      if (
        tryHandleCajaCerradaFromHttp(response.status, errorBody, {
          requestBody: resetPost.body,
        })
      ) {
        return { success: false, error: CAJA_CERRADA.MESSAGE };
      }

      let errorMessage = `HTTP ${response.status}`;
      
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorBody.substring(0, 200) || errorMessage;
      }

      const newStatus: SyncStatus = FATAL_ERROR_CODES.includes(response.status)
        ? 'error_fatal'
        : 'error';

      await updatePostStatus(resetPost, newStatus, errorMessage, response.status);
      
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
    return { success: false, error: errorMessage };
  }
};
